import { queryOptions } from "@tanstack/react-query";
import type {
  AdminEditMaterialLinesPayload,
  BikeId,
  CreateRequestBodySchema,
  EntityId,
  ExecuteTransitionResult,
  MaterialShipmentIncrements,
  RequestDto,
  RequestHydrated,
  TransitionDto,
  TransitionId,
} from "@yalana/contracts";
import { TransitionLogEntryDto } from "@yalana/contracts";
import { fetchApi } from "../../../shared/infrastructure/api-client";
import { toListSearchParams } from "../../../shared/utils/list-search-params";

type TransitionLogEntry = ReturnType<(typeof TransitionLogEntryDto)["parse"]>;

// Single source of truth: contracts/src/requests/requests.schema.ts (BEL-446).
type CreateRequestInput = CreateRequestBodySchema;

interface ExecuteTransitionInput {
  transitionId: TransitionId;
  executedByEntityId: EntityId;
  payloadUpdates?: Record<string, unknown>;
  materialLines?: { materialId: string; quantity: number }[];
  adminEdit?: AdminEditMaterialLinesPayload;
  shipmentIncrements?: MaterialShipmentIncrements;
}

interface PaginationParams {
  page: number;
  pageSize: number;
}

interface RequestListParams extends PaginationParams {
  locationId?: string | null;
  documentTypeId?: string | null;
  status?: "active" | "finished" | null;
  customerId?: string | null;
  q?: string | null;
  sort?: string | null;
  dir?: "asc" | "desc" | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const requestsQueries = {
  all: () => ["requests"] as const,
  list: (params: RequestListParams = { page: 1, pageSize: 10 }) =>
    queryOptions({
      queryKey: [...requestsQueries.all(), "list", params] as const,
      queryFn: () =>
        fetchApi<PaginatedResponse<RequestHydrated>>("/api/requests", {
          searchParams: toListSearchParams({
            page: params.page,
            pageSize: params.pageSize,
            locationId: params.locationId,
            documentTypeId: params.documentTypeId,
            status: params.status,
            customerId: params.customerId,
            q: params.q,
            sort: params.sort,
            dir: params.dir,
          }),
        }),
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: [...requestsQueries.all(), id] as const,
      queryFn: () => fetchApi<RequestHydrated>(`/api/requests/${id}`),
      enabled: !!id,
    }),
  transitions: (id: string) =>
    queryOptions({
      queryKey: [...requestsQueries.all(), id, "transitions"] as const,
      queryFn: () =>
        fetchApi<TransitionDto[]>(`/api/requests/${id}/transitions`),
      enabled: !!id,
    }),
  bikes: (id: string) =>
    queryOptions({
      queryKey: [...requestsQueries.all(), id, "bikes"] as const,
      queryFn: () => fetchApi<BikeId[]>(`/api/requests/${id}/bikes`),
      enabled: !!id,
    }),
  history: (id: string) =>
    queryOptions({
      queryKey: [...requestsQueries.all(), id, "history"] as const,
      queryFn: () =>
        fetchApi<TransitionLogEntry[]>(`/api/requests/${id}/history`),
      enabled: !!id,
    }),
};

export const requestsMutations = {
  create: (customerSlug: string) => ({
    mutationFn: (data: CreateRequestInput) =>
      fetchApi<{ request: RequestDto; document: unknown }>(
        `/api/c/${customerSlug}/requests`,
        { method: "POST", body: JSON.stringify(data) },
      ),
  }),
  executeTransition: (id: string) => ({
    mutationFn: (data: ExecuteTransitionInput) =>
      fetchApi<ExecuteTransitionResult>(`/api/requests/${id}/transitions`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  }),
  deactivate: (id: string) => ({
    mutationFn: () =>
      fetchApi<{ id: string }>(`/api/requests/${id}/deactivate`, {
        method: "PATCH",
      }),
  }),
};
